import * as React from 'react';
import { X, type LucideIcon } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import { toneIcon, toneRole, toneSurface } from '../../lib/tone';

/**
 * Inline alert — the handoff's section 02.
 *
 *   <Alert tone="warning" title="Two-hour minimum applies">
 *     The Meeting & Training Room bills a minimum of two hours.
 *   </Alert>
 *
 * Sits in the flow, directly above or below the thing it describes, and never
 * auto-dismisses — pass `onDismiss` to let the user close it themselves. One per area: if two conditions are true, show the blocking
 * one. A message about a single field belongs under that field; a message
 * about work that has finished is a toast.
 *
 * Every alert in the handoff is the same three parts — mark, title, body — so
 * this takes them as props rather than as sub-components. There is nothing to
 * assemble and nothing to get wrong.
 *
 * The surface, the mark and the aria role all come from `lib/tone`, which is
 * also where the toasts and the badges get theirs: an alert decides that it is
 * a warning, not what a warning looks like.
 */
const alertVariants = cva('flex w-full flex-wrap items-start gap-3 rounded-card border px-4 py-3', {
  variants: { tone: toneSurface },
  defaultVariants: { tone: 'neutral' },
});

function Alert({
  tone = 'neutral',
  title,
  icon,
  action,
  onDismiss,
  className,
  children,
  role,
  ...props
}: Omit<React.ComponentProps<'div'>, 'title'> &
  VariantProps<typeof alertVariants> & {
    title: React.ReactNode;
    /** Override the tone's mark, or pass `false` for no mark at all. */
    icon?: LucideIcon | false;
    /**
     * The one thing to do about this message — retry, pick another slot, go
     * pay. Optional, and worth leaving out when the alert is only telling the
     * user something: a button that does not need pressing still asks to be.
     */
    action?: React.ReactNode;
    /**
     * Shows a close button that calls this. The alert does not hide itself:
     * the caller owns whether it is shown, so it can also clear the condition
     * the alert was about.
     */
    onDismiss?: () => void;
  }) {
  const resolved = tone ?? 'neutral';
  const Icon = icon === false ? null : (icon ?? toneIcon[resolved]);

  return (
    <div
      data-slot="alert"
      data-tone={resolved}
      role={role ?? toneRole[resolved]}
      className={cn(alertVariants({ tone }), className)}
      {...props}
    >
      {Icon && <Icon className="size-4 shrink-0 translate-y-0.5" aria-hidden />}

      {/* `basis-64` is what makes the action wrap rather than crush the copy:
          the text claims a sensible minimum, and when the action can no longer
          sit beside it the whole row breaks instead of squeezing. */}
      <div className="flex min-w-0 grow basis-64 flex-col gap-1">
        <p className="text-md font-bold text-balance">{title}</p>
        {/* Body copy stays neutral rather than tinting with the tone. Coloured
            prose is harder to read, and the mark and title have already said
            which kind of message this is — colour is never the only signal. */}
        {children && <div className="text-sm text-ink-secondary text-pretty">{children}</div>}
      </div>

      {action && (
        <div data-slot="alert-action" className="ml-auto shrink-0 self-center">
          {action}
        </div>
      )}

      {onDismiss && (
        <button
          type="button"
          data-slot="alert-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-mr-1 shrink-0 rounded-sm p-1 opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        >
          <X className="size-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

export { Alert };
